import { useEffect, useState, useCallback } from "react";
import { api } from "@/lib/apiClient";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { CreditCard, Warning } from "@phosphor-icons/react";
import { toast } from "sonner";

const STATUS = {
  off: ["Disabled", "text-zinc-400"],
  pending: ["Enabled — reconciliation required", "text-amber-400"],
  active: ["Active", "text-emerald-400"],
};

const Results = ({ res }) => (
  <div className="mt-3 rounded-lg border border-white/10 divide-y divide-white/5 text-xs" data-testid="stripe-reconcile-results">
    <div className="px-3 py-2 text-zinc-400">{res.applied ? "Applied" : "Preview (nothing changed yet)"} · {res.results.length} subscription(s)</div>
    {res.results.map((r) => (
      <div key={r.org_id} className="px-3 py-2 flex flex-wrap gap-x-3">
        <span className="text-zinc-200">{r.name}</span>
        <span className="text-zinc-500">{r.plan}</span>
        <span className={r.decision === "downgrade" ? "text-red-400" : r.decision === "keep" ? "text-emerald-400" : "text-zinc-500"}>
          {r.decision === "downgrade" ? "would downgrade to Free" : r.decision}{r.status ? ` (${r.status})` : ""}
        </span>
        {r.internal_unlimited && <span className="text-fuchsia-400">Unlimited (Internal) kept regardless</span>}
      </div>
    ))}
  </div>
);

export function StripeCard() {
  const [st, setSt] = useState(null);
  const [res, setRes] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = useCallback(() => api.get("/admin/stripe/state").then((r) => setSt(r.data)).catch(() => {}), []);
  useEffect(() => { load(); }, [load]);
  if (!st) return null;
  const mode = !st.enabled ? "off" : st.reconcile_pending ? "pending" : "active";
  const toggle = async (v) => {
    const msg = v
      ? "Enable Stripe? Nothing is processed until you run and apply a reconciliation."
      : "Disable Stripe? Checkout, billing portal, sync and webhook processing stop immediately. Subscriptions in Stripe are NOT cancelled.";
    if (!window.confirm(msg)) return;
    try { await api.post("/admin/stripe/state", { enabled: v }); setRes(null); toast.success(v ? "Stripe enabled — reconciliation required" : "Stripe disabled"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const reconcile = async (apply) => {
    if (apply && !window.confirm("Apply reconciliation? Organizations whose Stripe subscription is no longer active will be downgraded (Unlimited (Internal) is never affected). Stripe then becomes active.")) return;
    setBusy(true);
    try { const r = await api.post(`/admin/stripe/reconcile?apply=${apply}`); setRes(r.data); load(); if (apply) toast.success("Stripe reconciled and active"); }
    catch (e) { toast.error(e?.response?.data?.detail || "Reconciliation failed"); }
    finally { setBusy(false); }
  };
  const billing = st.subscriptions.filter((s) => s.may_still_bill);
  return (
    <div className="rounded-xl border border-white/10 bg-zinc-900/60 p-4" data-testid="stripe-card">
      <div className="flex items-center gap-3">
        <CreditCard size={20} className="text-blue-400 shrink-0" />
        <div className="flex-1 min-w-0">
          <div className="text-sm font-medium">Stripe integration · <span data-testid="stripe-status" className={STATUS[mode][1]}>{STATUS[mode][0]}</span></div>
          <div className="text-xs text-zinc-500">When disabled, Controlly makes no Stripe calls and ignores webhooks ({st.ignored_events} ignored so far). Keys, subscription IDs and payment history are kept.</div>
        </div>
        <Switch data-testid="stripe-enabled-switch" checked={st.enabled} onCheckedChange={toggle} />
      </div>
      {mode === "pending" && (
        <div className="mt-3 flex flex-wrap gap-2">
          <Button size="sm" variant="outline" disabled={busy} data-testid="stripe-reconcile-preview" onClick={() => reconcile(false)} className="border-white/15 bg-transparent">Preview reconciliation</Button>
          <Button size="sm" disabled={busy} data-testid="stripe-reconcile-apply" onClick={() => reconcile(true)} className="bg-blue-600 hover:bg-blue-500">Apply &amp; activate Stripe</Button>
        </div>
      )}
      {res && <Results res={res} />}
      {billing.length > 0 && (
        <div className="mt-3 rounded-lg border border-amber-500/25 bg-amber-500/5 p-3 text-xs text-amber-200" data-testid="stripe-billing-risk">
          <div className="flex items-center gap-1.5 font-medium mb-1"><Warning size={14} weight="fill" /> Subscriptions that may still bill in Stripe</div>
          {billing.map((s) => (
            <div key={s.org_id} className="text-amber-200/80">{s.name} — {s.plan}{s.internal_unlimited ? " (Unlimited (Internal) assigned)" : ""} · <span className="font-mono">{s.stripe_subscription_id || s.snaplink_addon_sub_id}</span></div>
          ))}
          <div className="mt-1 text-amber-200/60">Controlly will not cancel these. Cancel them in the Stripe dashboard if you no longer want to be charged.</div>
        </div>
      )}
    </div>
  );
}
